/**
 * Phase 7 — Client-actor surfaces.
 * ClientEstimatePanel (post-accept), ClientInvoicePanel (post-completion),
 * ClientVariancePill (post-invoice).
 *
 * Locked Phase 7 r2 rules at the UI layer:
 *  - Estimate panel renders ONLY the §3.3.1 allow-list.
 *  - Invoice panel filters helper_labor_detail + contractor_platform_fee at render
 *    (defense-in-depth; backend already strips for Client).
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money, Stat } from "./_shared";


export function ClientEstimatePanel({ jobId, bookingId, bookingState }) {
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api(`/jobs/${jobId}/bookings/${bookingId}/estimate`);
        setEstimate(r.estimate);
      } catch { setEstimate(null); } finally { setLoading(false); }
    })();
  }, [jobId, bookingId, bookingState]);
  if (loading || !estimate) return null;
  return (
    <div className="avcp-card p-5" data-testid="client-estimate-panel">
      <h3 className="font-semibold mb-2">Your quote — estimated cost</h3>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        This is the confirmed cost estimate. The final invoice uses accepted individual rates and approved actual hours.
      </p>
      <div className="grid sm:grid-cols-3 gap-4 text-sm">
        <Stat label="Range" value={`${money(estimate.estimate_low_amount, estimate.estimate_currency)} – ${money(estimate.estimate_high_amount, estimate.estimate_currency)}`} />
        <Stat label="Indicative" value={money(estimate.estimate_indicative_amount, estimate.estimate_currency)} />
        <Stat label="Estimated hours" value={`${estimate.estimate_planned_hours}h`} />
        <Stat label="Crew size" value={`${estimate.estimate_crew_size}`} />
        <Stat label="Travel" value={estimate.estimate_travel_included ? "Included" : "Excluded"} />
        <Stat label="Platform fees" value={estimate.platform_fees_included ? "Included" : "Not included"} />
      </div>
      {estimate.revision_index > 0 && (
        <p className="text-xs text-amber-700 mt-3">
          Estimate revised (revision {estimate.revision_index}). View history on the Project page.
        </p>
      )}
    </div>
  );
}


export function ClientInvoicePanel({ jobId, bookingId, bookingState }) {
  const [invoice, setInvoice] = useState(null);
  const [variance, setVariance] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api(`/jobs/${jobId}/bookings/${bookingId}/invoice`);
        setInvoice(r);
        try {
          const v = await api(`/jobs/${jobId}/bookings/${bookingId}/variance`);
          setVariance(v);
        } catch { /* no variance yet */ }
      } catch { setInvoice(null); } finally { setLoading(false); }
    })();
  }, [jobId, bookingId, bookingState]);
  if (loading || !invoice) return null;
  // UI-layer defense-in-depth: skip forbidden kinds even if backend slipped.
  const safeLines = invoice.lines.filter((ln) =>
    !["helper_labor_detail", "contractor_platform_fee"].includes(ln.kind)
  );
  return (
    <div className="avcp-card p-5" data-testid="client-invoice-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold">Invoice</h3>
        <span className="avcp-pill" data-testid="invoice-state-pill">{invoice.state}</span>
      </div>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Issued by Workforce Partners. Currency: {invoice.currency}.
      </p>
      <table className="w-full text-sm">
        <thead className="text-xs text-[var(--avcp-ink-soft)]">
          <tr><th className="text-left">Item</th><th>Qty</th><th>Unit</th><th className="text-right">Amount</th></tr>
        </thead>
        <tbody>
          {safeLines.map((ln, i) => (
            <tr key={i} className="border-t border-[var(--avcp-line)]">
              <td className="py-2">{ln.description}</td>
              <td className="text-center">{ln.qty}</td>
              <td className="text-center">{money(ln.unit_amount, invoice.currency)}</td>
              <td className="text-right">{money(ln.line_amount, invoice.currency)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-[var(--avcp-line)] font-semibold">
            <td colSpan={3} className="py-2 text-right">Total</td>
            <td className="text-right">{money(invoice.total_amount, invoice.currency)}</td>
          </tr>
        </tfoot>
      </table>
      {variance && (
        <ClientVariancePill variance={variance} />
      )}
    </div>
  );
}


export function ClientVariancePill({ variance }) {
  if (!variance) return null;
  const bucket = variance.variance_bucket;
  const palette = bucket === "within_5pct" ? "bg-emerald-100 text-emerald-800"
                    : bucket.startsWith("over") ? "bg-amber-100 text-amber-800"
                    : "bg-sky-100 text-sky-800";
  const sign = variance.variance_amount >= 0 ? "+" : "";
  return (
    <div className={`mt-3 inline-block px-2 py-1 rounded text-xs ${palette}`}
         data-testid="client-variance-pill">
      vs Estimate: {sign}{money(variance.variance_amount, variance.currency)}
      {" "}({sign}{variance.variance_percent}%) · {bucket.replace(/_/g, " ")}
    </div>
  );
}
